// src/components/layout/Navbar.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate, NavLink, useLocation } from "react-router-dom";
import { FiShoppingCart, FiUser, FiMenu, FiX, FiBell, FiSearch, FiHeart, FiChevronDown, FiTag, FiShoppingBag, FiGrid } from "react-icons/fi";
import { BsShop } from "react-icons/bs";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useSettings } from "../../context/SettingsContext";
import SocialAuthButtons from "../auth/SocialAuthButtons";
import NotificationBell from "../common/NotificationBell";
import { normalizeImagePath } from "../../utils/imageUtils";
import API, { searchAPI, pageAPI } from "../../services/api";
import { toast } from "react-toastify";
import { useLanguage } from "../../i18n/LanguageContext";

const ANNOUNCEMENT_VERSION = "v1";
const BANNER_KEY = `mx_top_banner_closed_until_${ANNOUNCEMENT_VERSION}`;

const shouldShowBanner = () => {
  try {
    const until = Number(localStorage.getItem(BANNER_KEY) || 0);
    return Date.now() > until;
  } catch {
    return true;
  }
};

const hideBannerForHours = (hours = 24) => {
  try {
    const until = Date.now() + hours * 60 * 60 * 1000;
    localStorage.setItem(BANNER_KEY, String(until));
  } catch {}
};

const normalizeExternalUrl = (raw) => {
  const url = String(raw || "").trim();
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("//")) return `https:${url}`;
  return `https://${url}`;
};

const Navbar = ({ logoSrc: propLogoSrc }) => {
  const { t, lang, setLang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [headerPages, setHeaderPages] = useState([]);
  useEffect(() => {
    let alive = true;
    pageAPI.menu()
      .then((res) => {
        const data = (res && res.data && res.data.data) || [];
        if (alive) setHeaderPages(data.filter((p) => p.showInHeader));
      })
      .catch(() => { if (alive) setHeaderPages([]); });
    return () => { alive = false; };
  }, []);
  const cmsHref = (p) => (p.slug.startsWith("cgu/") ? `/${p.slug}` : `/p/${p.slug}`);
  const [profileOpen, setProfileOpen] = useState(false);
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // ✅ texte de recherche (auto)
  const [search, setSearch] = useState("");
  // P1-B : autocomplétion (deals / marchands / catégories)
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const searchBoxRef = useRef(null);

  const [activeHomeSection, setActiveHomeSection] = useState("");
  const [showBanner, setShowBanner] = useState(() => shouldShowBanner());

  const { user, logout, login } = useAuth();
  const { cartCount } = useCart();

  const profileRef = useRef(null);
  const authRef = useRef(null);
  const mountedRef = useRef(false);

  // Le personnel entre dans le meme back-office que l'administrateur :
  // sans cela, aucun lien « Administration » ne s'affichait pour lui et
  // le compte paraissait etre un simple compte client.
  const isAdmin = user?.role === "admin" || user?.role === "staff";
  // Un membre d'equipe a le role "user" : sans ce drapeau, aucun lien vers
  // la boutique ne s'affichait pour lui, alors que la route l'accepte deja.
  const isMerchant = user?.role === "merchant" || !!user?.isTeamMember;
  const isClient = !!user && !isAdmin && !isMerchant;

  const { settings } = useSettings();
  const currentLogo = (settings?.logo ? normalizeImagePath(settings.logo) : null) || propLogoSrc || `${process.env.PUBLIC_URL || ""}/logo.png`;

  const maxulaHouseUrl = useMemo(() => {
    const raw = (process.env.REACT_APP_MAXULA_HOUSE_URL || process.env.REACT_APP_HOUSE_URL || "").trim();
    return normalizeExternalUrl(raw);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (authRef.current && !authRef.current.contains(e.target)) {
        setAuthMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setProfileOpen(false);
    setAuthMenuOpen(false);
  }, [location.pathname, location.search]);

  // Connexion directe depuis le menu visiteur (comptes déjà existants)
  const handleGuestLogin = useCallback(async () => {
    const email = authEmail.trim();
    const password = authPassword;
    if (!email || !password) {
      setAuthError("Entrez votre e-mail et votre mot de passe.");
      return;
    }
    setAuthLoading(true);
    setAuthError("");
    try {
      await login(email, password);
      setAuthMenuOpen(false);
      setAuthPassword("");
      setAuthError("");
      toast.success("Connexion réussie");
    } catch (err) {
      setAuthError((err && err.message) || "E-mail ou mot de passe incorrect.");
    } finally {
      setAuthLoading(false);
    }
  }, [authEmail, authPassword, login]);

  useEffect(() => {
    let interval = null;
    if (user && isAdmin) {
      fetchNotificationCount();
      interval = setInterval(fetchNotificationCount, 30000);
    }
    return () => interval && clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const fetchNotificationCount = async () => {
    try {
      const res = await API.get("/admin/notifications/unread-count");
      if (!mountedRef.current) return;
      setUnreadNotifications(Number(res?.data?.count || 0));
    } catch {
      if (!mountedRef.current) return;
      setUnreadNotifications((prev) => prev || 0);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/login");
      window.scrollTo(0, 0);
    }
  };

  const userName =
    (user && (user.firstName || user.name || (user.email ? user.email.split("@")[0] : null))) ||
    "Mon compte";

  // ✅ on empêche enter de faire quelque chose (mais bouton search marche toujours)
  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    setShowSugg(false);
    setIsMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const goSuggestion = (sug) => {
    setShowSugg(false);
    setIsMenuOpen(false);
    if (sug.type === 'deal' && sug.slug) navigate(`/deals/${sug.slug}`);
    else if (sug.type === 'merchant') navigate(`/marchands/${sug.id}`);
    else if (sug.type === 'category') navigate(`/search?categoryId=${sug.id}`);
    else navigate(`/search?q=${encodeURIComponent(sug.label || '')}`);
  };

  // P1-B : AUTOCOMPLÉTION (sans navigation) — debounce 300 ms
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setSuggestions([]); setShowSugg(false); return; }
    const timer = setTimeout(() => {
      searchAPI.suggestions(q)
        .then((r) => {
          const list = r?.data?.data || [];
          setSuggestions(list);
          setShowSugg(list.length > 0);
        })
        .catch(() => { setSuggestions([]); setShowSugg(false); });
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fermer le dropdown au clic extérieur / Échap
  useEffect(() => {
    const onDown = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSugg(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowSugg(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, []);

  const navLinkClass = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium transition ${
      isActive ? "text-primary-600" : "text-gray-700 hover:text-primary-600"
    }`;

  const dealsBtnClass = (type) => {
    const params = new URLSearchParams(location.search);
    const sort = params.get("sort");
    const isActiveLink = location.pathname === "/deals" && sort === type;

    return `px-3 py-2 text-sm font-medium transition ${
      isActiveLink ? "text-primary-600" : "text-gray-700 hover:text-primary-600"
    }`;
  };

  const navBtnClass = (id) =>
    `px-3 py-2 text-sm font-medium transition ${
      location.pathname === "/" && activeHomeSection === id
        ? "text-primary-600"
        : "text-gray-700 hover:text-primary-600"
    }`;

  const scrollToSection = useCallback(
    (id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const headerOffset = showBanner ? 120 : 90;
      const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    },
    [showBanner]
  );

  const goHomeSection = (id) => (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    setProfileOpen(false);
    setActiveHomeSection(id);

    if (window.location.pathname === "/") {
      scrollToSection(id);
    } else {
      navigate("/");
      requestAnimationFrame(() => window.scrollTo(0, 0));
      setTimeout(() => scrollToSection(id), 120);
    }
  };

  useEffect(() => {
    if (location.pathname !== "/") setActiveHomeSection("");
  }, [location.pathname]);

  const closeBanner = () => {
    setShowBanner(false);
    hideBannerForHours(24);
  };

  return (
    <header className="sticky top-0 z-50">
      {showBanner && (
        <div className="bg-[#7a1147] text-white border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <span
                  className="hidden md:block text-sm leading-snug overflow-hidden"
                  style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                >
                  🎫 Validité des Coupons — Tous les coupons Maxula sont valables 60 jours à partir de la date d’achat.
                  Vous pouvez offrir le coupon (excellent cadeau). Après paiement, le nom + les coordonnées du partenaire
                  seront envoyés automatiquement. Tous les détails (adresse, conditions, validité) seront indiqués sur
                  le coupon reçu par WhatsApp. 📞 Contact : 0606 06 61 62 – 0539 34 24 94 (11h–20h). ⚠ Maxula décline
                  toute responsabilité : seul le partenaire est responsable.
                </span>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <Link
                  to="/annonces"
                  onClick={() => setIsMenuOpen(false)}
                  className="text-[11px] sm:text-sm font-semibold underline underline-offset-2 whitespace-nowrap"
                >
                  Lire plus
                </Link>

                <button
                  type="button"
                  onClick={closeBanner}
                  className="text-white/90 hover:text-white text-lg leading-none"
                  aria-label="Fermer"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center shrink-0">
              <img
                src={currentLogo}
                alt="Maxula"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/logo.png`;
                }}
                className="h-10 w-auto object-contain"
              />
            </Link>

            {/* DESKTOP SEARCH — P1-B : autocomplétion + page /search */}
            <form onSubmit={onSearchSubmit} className="hidden md:flex flex-1 max-w-2xl">
              <div className="w-full relative" ref={searchBoxRef}>
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => { if (suggestions.length > 0) setShowSugg(true); }}
                  placeholder={t('nav.searchPlaceholder')}
                  className="w-full pl-10 pr-12 py-2 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full
                             bg-[#e11d7b] text-white flex items-center justify-center
                             hover:bg-[#c91469] transition"
                  aria-label="Rechercher"
                >
                  <FiSearch />
                </button>

                {/* Dropdown de suggestions, groupé par type */}
                {showSugg && suggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden z-50">
                    {['deal', 'merchant', 'category'].map((type) => {
                      const group = suggestions.filter((x) => x.type === type);
                      if (!group.length) return null;
                      const meta = type === 'deal'
                        ? { label: t('sugg.deals'), Icon: FiTag }
                        : type === 'merchant'
                          ? { label: t('sugg.merchants'), Icon: FiShoppingBag }
                          : { label: t('sugg.categories'), Icon: FiGrid };
                      return (
                        <div key={type} className="py-1">
                          <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{meta.label}</div>
                          {group.map((sug) => (
                            <button key={`${type}-${sug.id}`} type="button" onClick={() => goSuggestion(sug)}
                              className="w-full flex items-center gap-2.5 px-4 py-2 text-left text-sm text-gray-700 hover:bg-primary-50">
                              <meta.Icon size={14} className="text-primary-500 flex-shrink-0" />
                              <span className="truncate">{sug.label}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                    <button type="button" onClick={onSearchSubmit}
                      className="w-full px-4 py-2.5 text-left text-sm font-semibold text-primary-600 border-t border-gray-100 hover:bg-primary-50">
                      {t('nav.seeAllResults', { q: search.trim() })}
                    </button>
                  </div>
                )}
              </div>
            </form>

            {/* P2-A : sélecteur de langue FR | AR */}
            <div className="hidden md:flex items-center mr-2 rounded-full border border-gray-200 overflow-hidden text-xs font-bold">
              <button type="button" onClick={() => setLang('fr')}
                className={`px-2.5 py-1.5 ${lang === 'fr' ? 'bg-[#e11d7b] text-white' : 'text-gray-600 hover:bg-gray-50'}`}>FR</button>
              <button type="button" onClick={() => setLang('ar')}
                className={`px-2.5 py-1.5 ${lang === 'ar' ? 'bg-[#e11d7b] text-white' : 'text-gray-600 hover:bg-gray-50'}`}>AR</button>
            </div>

            <div className="hidden md:flex items-center gap-3">
              {!user && (
                <Link
                  to="/creer-un-deal"
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-gray-300 text-sm font-semibold text-gray-800 hover:border-[#e11d7b] hover:text-[#e11d7b] transition whitespace-nowrap"
                >
                  <BsShop size={17} /> {lang === 'ar' ? 'بِع على Maxula' : 'Vendre sur Maxula'}
                </Link>
              )}
              {user ? (
                <>
                  {isAdmin ? (
                    <Link
                      to="/admin/notifications"
                      className="relative p-2 text-gray-700 hover:text-primary-600"
                      aria-label="Notifications"
                    >
                      <FiBell size={22} />
                      {unreadNotifications > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                          {unreadNotifications > 9 ? "9+" : unreadNotifications}
                        </span>
                      )}
                    </Link>
                  ) : (
                    <>
                      <NotificationBell lang={lang} isRTL={isRTL} />
                      <Link to="/cart" className="relative p-2 text-gray-700 hover:text-primary-600" aria-label="Panier">
                        <FiShoppingCart size={22} />
                        {cartCount > 0 && (
                          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-primary-600 rounded-full">
                            {cartCount}
                          </span>
                        )}
                      </Link>
                    </>
                  )}

                  <div className="relative" ref={profileRef}>
                    <button
                      type="button"
                      onClick={() => setProfileOpen((v) => !v)}
                      aria-expanded={profileOpen}
                      aria-haspopup="menu"
                      className="flex items-center gap-2 text-gray-700 hover:text-primary-600"
                    >
                      <FiUser size={20} />
                      <span className="font-medium">{userName}</span>
                    </button>

                    {profileOpen && (
                      <div
                        role="menu"
                        aria-orientation="vertical"
                        className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-56 bg-white rounded-lg shadow-lg py-2 border`}
                      >
                        {isClient && (
                          <>
                            <Link
                              to="/dashboard"
                              onClick={() => setProfileOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Mon profil
                            </Link>
                            <Link
                              to="/dashboard/orders"
                              onClick={() => setProfileOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Mes commandes
                            </Link>
                            <Link
                              to="/messages"
                              onClick={() => setProfileOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Mes messages
                            </Link>
                            <Link
                              to="/dashboard/vouchers"
                              onClick={() => setProfileOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Mes vouchers
                            </Link>
                            <Link
                              to="/dashboard/favorites"
                              onClick={() => setProfileOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Mes favoris
                            </Link>
                          </>
                        )}

                        {isAdmin && (
                          <Link
                            to="/admin"
                            onClick={() => setProfileOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Dashboard Admin
                          </Link>
                        )}

                        {isMerchant && (
                          <Link
                            to="/merchant"
                            onClick={() => setProfileOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Dashboard Marchand
                          </Link>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            handleLogout();
                            setProfileOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          Déconnexion
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Favoris */}
                  <button
                    type="button"
                    onClick={() => setAuthMenuOpen(true)}
                    className="p-2 text-gray-700 hover:text-primary-600"
                    aria-label="Favoris"
                  >
                    <FiHeart size={22} />
                  </button>

                  {/* Notifications */}
                  <button
                    type="button"
                    onClick={() => setAuthMenuOpen(true)}
                    className="p-2 text-gray-700 hover:text-primary-600"
                    aria-label="Notifications"
                  >
                    <FiBell size={22} />
                  </button>

                  {/* Panier */}
                  <Link to="/cart" className="relative p-2 text-gray-700 hover:text-primary-600" aria-label="Panier">
                    <FiShoppingCart size={22} />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-primary-600 rounded-full">
                        {cartCount}
                      </span>
                    )}
                  </Link>

                  {/* Compte (connexion / inscription) */}
                  <div className="relative" ref={authRef}>
                    <button
                      type="button"
                      onClick={() => setAuthMenuOpen((v) => !v)}
                      aria-expanded={authMenuOpen}
                      aria-haspopup="menu"
                      className="flex items-center gap-1 p-2 text-gray-700 hover:text-primary-600 border border-gray-200 rounded-full"
                      aria-label="Mon compte"
                    >
                      <FiUser size={20} />
                      <FiChevronDown size={16} />
                    </button>

                    {authMenuOpen && (
                      <div
                        role="menu"
                        className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-white rounded-xl shadow-xl border p-5 z-50`}
                      >
                        <h3 className="text-lg font-bold text-center text-gray-900">Se connecter ou s'inscrire</h3>

                        <div className="mt-4">
                          <SocialAuthButtons />
                        </div>

                        <div className="flex items-center gap-3 my-4">
                          <span className="flex-1 h-px bg-gray-200" />
                          <span className="text-xs text-gray-500">Ou avec un e-mail</span>
                          <span className="flex-1 h-px bg-gray-200" />
                        </div>

                        {authError && (
                          <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {authError}
                          </div>
                        )}

                        <input
                          type="email"
                          value={authEmail}
                          onChange={(e) => setAuthEmail(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleGuestLogin(); }}
                          placeholder="E-mail"
                          autoComplete="email"
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-200 mb-3"
                        />
                        <input
                          type="password"
                          value={authPassword}
                          onChange={(e) => setAuthPassword(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleGuestLogin(); }}
                          placeholder="Mot de passe"
                          autoComplete="current-password"
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-200 mb-1"
                        />
                        <div className="text-right mb-3">
                          <Link to="/forgot-password" onClick={() => setAuthMenuOpen(false)} className="text-xs text-primary-600 hover:underline">
                            Mot de passe oublié ?
                          </Link>
                        </div>
                        <button
                          type="button"
                          onClick={handleGuestLogin}
                          disabled={authLoading}
                          className="w-full px-4 py-2.5 rounded-full bg-primary-600 text-white font-semibold hover:bg-primary-700 transition disabled:opacity-60"
                        >
                          {authLoading ? "Connexion…" : "Se connecter"}
                        </button>

                        <p className="text-center text-sm text-gray-600 mt-4">
                          Pas encore de compte ?{" "}
                          <Link to="/register" onClick={() => setAuthMenuOpen(false)} className="text-primary-600 font-medium hover:underline">
                            S'inscrire
                          </Link>
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => setIsMenuOpen((v) => !v)}
              className="md:hidden p-2 text-gray-700"
              aria-label="Ouvrir le menu"
            >
              {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>

          {/* MOBILE SEARCH */}
          <form onSubmit={onSearchSubmit} className="md:hidden pb-3">
            <div className="w-full relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('nav.searchPlaceholder')}
                className="w-full pl-10 pr-12 py-2 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full
                           bg-[#e11d7b] text-white flex items-center justify-center
                           hover:bg-[#c91469] transition"
                aria-label="Rechercher"
              >
                <FiSearch />
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="border-t bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-1">
              <NavLink to="/" className={navLinkClass}>
                {t('nav.home')}
              </NavLink>

              <button type="button" onClick={goHomeSection("categories")} className={navBtnClass("categories")}>
                {t('nav.categories')}
              </button>

              <button type="button" onClick={goHomeSection("cities")} className={navBtnClass("cities")}>
                {t('nav.cities')}
              </button>

              <Link to="/deals?sort=newest" className={dealsBtnClass("newest")}>
                {t('nav.newest')}
              </Link>

              <Link to="/deals?sort=discount" className={dealsBtnClass("discount")}>
                {t('nav.bestOffers')}
              </Link>

              <NavLink to="/marchands" className={navLinkClass}>
                {t('nav.merchants')}
              </NavLink>

              <NavLink to="/tarifs" className={navLinkClass}>
                {t('nav.subscriptions')}
              </NavLink>

              <NavLink to="/support" className={navLinkClass}>
                {t('nav.support')}
              </NavLink>

              {maxulaHouseUrl && (
                <a
                  href={maxulaHouseUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-2 text-sm font-medium transition text-gray-700 hover:text-primary-600"
                >
                  Maxula House
                </a>
              )}

              {headerPages.map((p) => (
                <NavLink key={p.slug} to={cmsHref(p)} className={navLinkClass}>
                  {lang === 'ar' && p.titleAr ? p.titleAr : p.title}
                </NavLink>
              ))}
            </div>
          </div>

          {isMenuOpen && (
            <div className="md:hidden py-3 border-t">
              <div className="flex flex-col">
                <NavLink to="/" onClick={() => setIsMenuOpen(false)} className={navLinkClass}>
                  Accueil
                </NavLink>

                <button type="button" onClick={goHomeSection("categories")} className={`${navBtnClass("categories")} text-left`}>
                  Catégories
                </button>

                <button type="button" onClick={goHomeSection("cities")} className={`${navBtnClass("cities")} text-left`}>
                  Villes
                </button>

                <Link to="/deals?sort=newest" onClick={() => setIsMenuOpen(false)} className={dealsBtnClass("newest")}>
                  Nouveautés
                </Link>

                <Link to="/deals?sort=discount" onClick={() => setIsMenuOpen(false)} className={dealsBtnClass("discount")}>
                  Meilleures offres
                </Link>

                <NavLink to="/marchands" onClick={() => setIsMenuOpen(false)} className={navLinkClass}>
                  Marchands
                </NavLink>

                <NavLink to="/tarifs" onClick={() => setIsMenuOpen(false)} className={navLinkClass}>
                  Abonnements
                </NavLink>

                <NavLink to="/support" onClick={() => setIsMenuOpen(false)} className={navLinkClass}>
                  Support
                </NavLink>

                {!user && (
                  <Link
                    to="/creer-un-deal"
                    onClick={() => setIsMenuOpen(false)}
                    className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-gray-300 text-sm font-semibold text-gray-800 hover:border-[#e11d7b] hover:text-[#e11d7b] transition"
                  >
                    <BsShop size={17} /> {lang === 'ar' ? 'بِع على Maxula' : 'Vendre sur Maxula'}
                  </Link>
                )}

                {maxulaHouseUrl && (
                  <a
                    href={maxulaHouseUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600"
                  >
                    Maxula House
                  </a>
                )}

                <div className="my-2 border-t" />

                {user ? (
                  <>
                    {!isAdmin && (
                      <Link to="/cart" onClick={() => setIsMenuOpen(false)} className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">
                        Panier {cartCount > 0 && `(${cartCount})`}
                      </Link>
                    )}

                    {isAdmin && (
                      <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">
                        Dashboard Admin
                      </Link>
                    )}

                    {isMerchant && (
                      <Link to="/merchant" onClick={() => setIsMenuOpen(false)} className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">
                        Dashboard Marchand
                      </Link>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="px-3 py-2 text-sm font-medium text-left text-red-600 hover:bg-red-50 rounded"
                    >
                      Déconnexion
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Link to="/cart" onClick={() => setIsMenuOpen(false)} className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600">
                      Panier {cartCount > 0 && `(${cartCount})`}
                    </Link>
                    <div className="px-3 pt-2">
                      <SocialAuthButtons />
                    </div>
                    <div className="flex items-center gap-3 px-3 pt-3">
                      <Link to="/login" onClick={() => setIsMenuOpen(false)} className="text-gray-700 hover:text-primary-600 font-medium">
                        Connexion
                      </Link>
                      <Link to="/register" onClick={() => setIsMenuOpen(false)} className="px-4 py-2 rounded-full bg-primary-600 text-white hover:bg-primary-700 font-medium">
                        Inscription
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
